import { useState } from "react";
import { IconLock, IconShieldCog } from "@tabler/icons-react";
import { Badge, Button, EditRowActions, Group, Input, Td, Text, Tr, useConfirmDialog } from "@/components/ui";
import type { RolAdmin } from "../services/roles-service";
import { useEditarRol, useEliminarRol } from "../hooks/use-roles-mutations";
import { RolPermisosDialog } from "./RolPermisosDialog";

const NOMBRE_RE = /^[A-Za-z0-9_]+$/;

/**
 * Fila de un rol · alterna vista/edición inline. Los roles del SISTEMA
 * (`esSistema`) solo permiten editar la descripción (el nombre queda bloqueado)
 * y no se pueden eliminar. Los personalizados permiten renombrar y eliminar
 * (este último solo si no tiene usuarios asignados · el backend lo valida).
 */
export function RolRow({ rol }: { rol: RolAdmin }) {
  const [editando, setEditando] = useState(false);
  const [permisosOpen, setPermisosOpen] = useState(false);
  const [nombre, setNombre] = useState(rol.nombre);
  const [descripcion, setDescripcion] = useState(rol.descripcion ?? "");
  const confirm = useConfirmDialog();

  const guardar = useEditarRol({ onEditado: () => setEditando(false) });
  const eliminar = useEliminarRol();

  const cancelar = () => {
    setNombre(rol.nombre);
    setDescripcion(rol.descripcion ?? "");
    setEditando(false);
  };

  const submitEdicion = () => {
    const n = rol.esSistema ? rol.nombre : nombre.trim();
    if (!rol.esSistema && !NOMBRE_RE.test(n)) return;
    guardar.mutate({ id: rol.id, input: { nombre: n, descripcion: descripcion.trim() || null } });
  };

  const confirmarEliminar = async () => {
    const ok = await confirm({
      title: "Eliminar rol",
      message: (
        <>
          ¿Eliminar el rol <b>{rol.nombre}</b>? Esta acción no se puede deshacer.
        </>
      ),
      confirmLabel: "Eliminar",
      confirmColor: "peligro",
    });
    if (ok) eliminar.mutate(rol.id);
  };

  if (!editando) {
    return (
      <Tr>
        <Td>
          <Group gap={6} align="center">
            {rol.esSistema ? <IconLock size={14} className="text-muted-foreground" /> : null}
            <Text size="sm" fw={500}>
              {rol.nombre}
            </Text>
          </Group>
        </Td>
        <Td>
          <Text size="sm" c={rol.descripcion ? undefined : "dimmed"}>
            {rol.descripcion ?? "—"}
          </Text>
        </Td>
        <Td>
          <Badge size="sm" color={rol.esSistema ? "info" : "slate"}>
            {rol.esSistema ? "Sistema" : "Personalizado"}
          </Badge>
        </Td>
        <Td align="center">
          <Text size="sm" className="tabular-nums">
            {rol.usuariosCount}
          </Text>
        </Td>
        <Td>
          <Button
            size="xs"
            variant="light"
            leftSection={rol.esSistema ? <IconLock size={13} stroke={1.8} /> : <IconShieldCog size={14} stroke={1.7} />}
            onClick={() => setPermisosOpen(true)}
          >
            {rol.permisos.length} {rol.esSistema ? "· ver" : "· editar"}
          </Button>
        </Td>
        <Td align="right">
          <EditRowActions
            mode="view"
            onEdit={() => setEditando(true)}
            onDelete={rol.esSistema ? undefined : confirmarEliminar}
            deleting={eliminar.isPending}
          />
        </Td>
        {permisosOpen ? <RolPermisosDialog rol={rol} open={permisosOpen} onClose={() => setPermisosOpen(false)} /> : null}
      </Tr>
    );
  }

  return (
    <Tr className="bg-muted/50">
      <Td>
        <Input
          size="xs"
          value={nombre}
          onChange={(e) => setNombre(e.currentTarget.value)}
          disabled={rol.esSistema}
          title={rol.esSistema ? "Los roles del sistema no se pueden renombrar" : undefined}
          autoFocus={!rol.esSistema}
        />
      </Td>
      <Td>
        <Input
          size="xs"
          value={descripcion}
          onChange={(e) => setDescripcion(e.currentTarget.value)}
          placeholder="Descripción (opcional)"
          maxLength={300}
          autoFocus={rol.esSistema}
        />
      </Td>
      <Td>
        <Badge size="sm" color={rol.esSistema ? "info" : "slate"}>
          {rol.esSistema ? "Sistema" : "Personalizado"}
        </Badge>
      </Td>
      <Td align="center">
        <Text size="sm" className="tabular-nums">
          {rol.usuariosCount}
        </Text>
      </Td>
      <Td>
        <Text size="xs" c="dimmed">
          {rol.permisos.length}
        </Text>
      </Td>
      <Td align="right">
        <EditRowActions mode="edit" saving={guardar.isPending} onCancel={cancelar} onSave={submitEdicion} />
      </Td>
    </Tr>
  );
}
